// src/registered/site-admin/meter_catalogue/MeterCatalogueController.js
//
// Site-admin curator (Group ESG) management of the global Meter Data Catalogue
// and the meter register. Owns all state, returns a flat props object for the
// view — same controller shape as knowledge_base / report_component_library.

import { useCallback, useEffect, useMemo, useState } from "react";
import { GoldenSourceHelper } from "../../../app/Switch_Core_React_Standards";
import MeterConceptDTO, {
  MeterConceptIdentifierDTO,
  MeterRegistrationDTO,
  COMMODITIES,
  CONCEPT_STATUSES,
  parseObis,
} from "./MeterCatalogueDTO";
import { validateConcept, validateIdentifier, validateRegistration } from "./MeterCatalogueValidator";
import {
  fetchRefLists,
  fetchConcepts,
  getConcept,
  addConcept,
  updateConcept,
  removeConcept,
  addIdentifier,
  removeIdentifier,
  setAttributeMap,
  removeAttributeMap,
  fetchRegistrations,
  addRegistration,
  updateRegistration,
  removeRegistration,
} from "./meterCatalogueInteractionService";

const G = (row, key) => GoldenSourceHelper.get(row, key);

const EMPTY_IDENTIFIER = () => new MeterConceptIdentifierDTO();

export default function MeterCatalogueController(siteKey) {
  const config = useMemo(() => ({ siteKey }), [siteKey]);

  const [tab, setTab] = useState("catalogue"); // "catalogue" | "meters"

  const [ref, setRef] = useState({
    commodities: COMMODITIES,
    schemes: [],
    statuses: CONCEPT_STATUSES,
    methods: [],
    units: [],
  });

  // --- catalogue -------------------------------------------------------------
  const [concepts, setConcepts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [commodityFilter, setCommodityFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");

  const [draft, setDraft] = useState(null); // MeterConceptDTO | null
  const [isNew, setIsNew] = useState(false);
  const [conceptErrors, setConceptErrors] = useState({});
  const [idForm, setIdForm] = useState(EMPTY_IDENTIFIER());
  const [idErrors, setIdErrors] = useState({});
  const [attrForm, setAttrForm] = useState({ attributeDefinitionId: "", confidence: "" });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  // --- meters --------------------------------------------------------------
  const [registrations, setRegistrations] = useState([]);
  const [regLoading, setRegLoading] = useState(false);
  const [regDraft, setRegDraft] = useState(null); // MeterRegistrationDTO | null
  const [regIsNew, setRegIsNew] = useState(false);
  const [regErrors, setRegErrors] = useState({});

  // --- loads -------------------------------------------------------------

  useEffect(() => {
    let alive = true;
    (async () => {
      const r = await fetchRefLists(config);
      if (!alive) return;
      setRef((prev) => ({
        commodities: r.commodities.length
          ? r.commodities.map((x) => ({ code: G(x, "CommodityCode"), label: G(x, "Label") }))
          : prev.commodities,
        schemes: r.schemes.map((x) => ({ code: G(x, "SchemeCode"), label: G(x, "Label") })),
        statuses: r.statuses.length
          ? r.statuses.map((x) => ({ code: G(x, "StatusCode"), label: G(x, "Label") }))
          : prev.statuses,
        methods: r.methods.map((x) => ({ code: G(x, "MethodCode"), label: G(x, "Label") })),
        units: r.units.map((x) => ({
          code: G(x, "UnitCode"),
          label: G(x, "UnitLabel"),
          family: G(x, "UnitFamily"),
        })),
      }));
    })();
    return () => {
      alive = false;
    };
  }, [config]);

  const refreshConcepts = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const rows = await fetchConcepts(
        { commodityCode: commodityFilter || null, statusCode: statusFilter || null, search: search || null },
        config,
      );
      setConcepts(
        (rows || []).map((r) => ({
          meterConceptId: G(r, "MeterConceptId"),
          conceptKey: G(r, "ConceptKey"),
          label: G(r, "Label"),
          commodityCode: G(r, "CommodityCode"),
          canonicalUnitCode: G(r, "CanonicalUnitCode"),
          canonicalUnitLabel: G(r, "CanonicalUnitLabel") || G(r, "CanonicalUnitCode"),
          statusCode: G(r, "StatusCode"),
          identifierCount: G(r, "IdentifierCount") || 0,
          attributeMapCount: G(r, "AttributeMapCount") || 0,
        })),
      );
    } catch (err) {
      setError(err?.message || "Could not load the catalogue.");
    } finally {
      setLoading(false);
    }
  }, [config, commodityFilter, statusFilter, search]);

  useEffect(() => {
    refreshConcepts();
  }, [refreshConcepts]);

  const refreshRegistrations = useCallback(async () => {
    setRegLoading(true);
    setError("");
    try {
      const rows = await fetchRegistrations({}, config);
      setRegistrations(
        (rows || []).map((r) => ({
          meterRegistrationId: G(r, "MeterRegistrationId"),
          meterSerial: G(r, "MeterSerial"),
          corporateEntityId: G(r, "CorporateEntityId"),
          siteReference: G(r, "SiteReference") || "",
          commodityCode: G(r, "CommodityCode"),
          schemeCode: G(r, "SchemeCode") || "",
          meterTimeZone: G(r, "MeterTimeZone") || "",
          description: G(r, "Description") || "",
          readingCount: G(r, "ReadingCount") || 0,
        })),
      );
    } catch (err) {
      setError(err?.message || "Could not load meters.");
    } finally {
      setRegLoading(false);
    }
  }, [config]);

  useEffect(() => {
    if (tab === "meters") refreshRegistrations();
  }, [tab, refreshRegistrations]);

  // --- concept editing --------------------------------------------------

  const newConcept = () => {
    setDraft(new MeterConceptDTO());
    setIsNew(true);
    setConceptErrors({});
    setIdForm(EMPTY_IDENTIFIER());
    setError("");
  };

  const editConcept = async (id) => {
    setError("");
    try {
      const full = await getConcept(id, config);
      if (!full) {
        setError(`Could not open concept ${id}.`);
        return;
      }
      const c = full.concept || {};
      setDraft(
        new MeterConceptDTO({
          meterConceptId: G(c, "MeterConceptId") || id,
          conceptKey: G(c, "ConceptKey"),
          label: G(c, "Label"),
          description: G(c, "Description"),
          commodityCode: G(c, "CommodityCode"),
          quantity: G(c, "Quantity"),
          electricalCharacteristic: G(c, "ElectricalCharacteristic"),
          direction: G(c, "Direction"),
          phase: G(c, "Phase"),
          timeCharacteristic: G(c, "TimeCharacteristic"),
          canonicalUnitCode: G(c, "CanonicalUnitCode"),
          defaultAggregationMethodCode: G(c, "DefaultAggregationMethodCode"),
          readingKindHint: G(c, "ReadingKindHint"),
          statusCode: G(c, "StatusCode"),
          replacedByMeterConceptId: G(c, "ReplacedByMeterConceptId"),
          identifiers: (full.identifiers || []).map((i) => ({
            identifierId: G(i, "IdentifierId"),
            schemeCode: G(i, "SchemeCode"),
            displayCode: G(i, "DisplayCode"),
            mappingContext: G(i, "MappingContext"),
            standard: G(i, "Standard"),
            statusCode: G(i, "StatusCode"),
            mappingMethod: G(i, "MappingMethod"),
            confidence: G(i, "Confidence"),
            verified: !!G(i, "Verified"),
            notes: G(i, "Notes"),
          })),
          attributeMaps: (full.attributeMaps || []).map((m) => ({
            mapId: G(m, "MapId"),
            attributeDefinitionId: G(m, "AttributeDefinitionId"),
            confidence: G(m, "Confidence"),
            notes: G(m, "Notes"),
          })),
          aliases: (full.aliases || []).map((a) => G(a, "AliasText")),
        }),
      );
      setIsNew(false);
      setConceptErrors({});
      setIdForm(EMPTY_IDENTIFIER());
    } catch (err) {
      setError(err?.message || "Could not open the concept.");
    }
  };

  const cancelEdit = () => {
    setDraft(null);
    setIsNew(false);
    setConceptErrors({});
  };

  const setConceptField = (field, value) =>
    setDraft((d) => {
      if (!d) return d;
      const next = new MeterConceptDTO(JSON.parse(JSON.stringify(d)));
      next[field] = value;
      return next;
    });

  const saveConcept = async () => {
    if (!draft) return;
    const { isValid, errors } = validateConcept(draft, concepts);
    setConceptErrors(errors);
    if (!isValid) return;

    setSaving(true);
    setError("");
    setNotice("");
    try {
      const base = {
        Label: draft.label.trim(),
        CommodityCode: draft.commodityCode,
        Quantity: draft.quantity,
        CanonicalUnitCode: draft.canonicalUnitCode,
        ElectricalCharacteristic: draft.electricalCharacteristic || null,
        Direction: draft.direction || null,
        Phase: draft.phase || null,
        TimeCharacteristic: draft.timeCharacteristic || null,
        DefaultAggregationMethodCode: draft.defaultAggregationMethodCode,
        ReadingKindHint: draft.readingKindHint,
        Description: draft.description?.trim() || null,
        StatusCode: draft.statusCode,
      };
      if (isNew) {
        await addConcept({ ...base, ConceptKey: draft.conceptKey.trim() }, config);
      } else {
        await updateConcept(
          {
            ...base,
            MeterConceptId: draft.meterConceptId,
            ReplacedByMeterConceptId: draft.replacedByMeterConceptId || null,
          },
          config,
        );
      }
      await refreshConcepts();
      setNotice(`Saved "${draft.conceptKey}".`);
      if (isNew) {
        // reopen the freshly created row so identifiers can be added
        const created = (await fetchConcepts({ search: draft.conceptKey }, config))[0];
        if (created) await editConcept(G(created, "MeterConceptId"));
      }
    } catch (err) {
      setError(err?.message || "Could not save the concept.");
    } finally {
      setSaving(false);
    }
  };

  const retireConcept = async (id) => {
    setError("");
    try {
      await removeConcept(id, config);
      if (draft?.meterConceptId === id) cancelEdit();
      await refreshConcepts();
      setNotice("Concept retired.");
    } catch (err) {
      setError(err?.message || "Could not retire the concept.");
    }
  };

  // --- identifier sub-form (only on a saved concept) ---------------------

  const setIdField = (field, value) =>
    setIdForm((f) => {
      const next = { ...f, [field]: value };
      if (field === "displayCode" && next.schemeCode === "OBIS") {
        const p = parseObis(value);
        if (p) Object.assign(next, { obisA: p.a, obisB: p.b, obisC: p.c, obisD: p.d, obisE: p.e, obisF: p.f });
      }
      return next;
    });

  const addIdentifierRow = async () => {
    if (!draft || isNew) return;
    const { isValid, errors } = validateIdentifier(idForm, draft.identifiers);
    setIdErrors(errors);
    if (!isValid) return;
    setSaving(true);
    setError("");
    try {
      const p = idForm.schemeCode === "OBIS" ? parseObis(idForm.displayCode) : null;
      await addIdentifier(
        {
          MeterConceptId: draft.meterConceptId,
          SchemeCode: idForm.schemeCode,
          DisplayCode: idForm.displayCode.trim(),
          MappingContext: (idForm.mappingContext || "abstract").trim(),
          ObisA: p?.a ?? null, ObisB: p?.b ?? null, ObisC: p?.c ?? null,
          ObisD: p?.d ?? null, ObisE: p?.e ?? null, ObisF: p?.f ?? null,
          Standard: idForm.standard?.trim() || null,
          StatusCode: idForm.statusCode || "candidate",
          MappingMethod: idForm.mappingMethod || "MANUAL",
          Confidence: idForm.confidence === "" ? null : Number(idForm.confidence),
          Notes: idForm.notes?.trim() || null,
        },
        config,
      );
      await editConcept(draft.meterConceptId);
      setIdForm(EMPTY_IDENTIFIER());
      setIdErrors({});
      setNotice("Identifier mapped.");
    } catch (err) {
      setError(err?.message || "Could not add the identifier.");
    } finally {
      setSaving(false);
    }
  };

  const removeIdentifierRow = async (identifierId) => {
    if (!draft) return;
    setError("");
    try {
      await removeIdentifier(identifierId, config);
      await editConcept(draft.meterConceptId);
      setNotice("Identifier removed.");
    } catch (err) {
      setError(err?.message || "Could not remove the identifier.");
    }
  };

  // --- attribute link -------------------------------------------------------

  const setAttrField = (field, value) => setAttrForm((f) => ({ ...f, [field]: value }));

  const linkAttribute = async () => {
    if (!draft || isNew) return;
    const attrId = (attrForm.attributeDefinitionId || "").trim();
    if (!attrId) {
      setError("Enter an AttributeDefinition id (GUID).");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await setAttributeMap(
        {
          MeterConceptId: draft.meterConceptId,
          AttributeDefinitionId: attrId,
          Confidence: attrForm.confidence === "" ? null : Number(attrForm.confidence),
        },
        config,
      );
      await editConcept(draft.meterConceptId);
      setAttrForm({ attributeDefinitionId: "", confidence: "" });
      setNotice("Attribute linked.");
    } catch (err) {
      setError(err?.message || "Could not link the attribute.");
    } finally {
      setSaving(false);
    }
  };

  const unlinkAttribute = async (mapId) => {
    if (!draft) return;
    setError("");
    try {
      await removeAttributeMap(mapId, config);
      await editConcept(draft.meterConceptId);
      setNotice("Attribute unlinked.");
    } catch (err) {
      setError(err?.message || "Could not unlink the attribute.");
    }
  };

  // --- meter registrations -------------------------------------------------

  const newRegistration = () => {
    setRegDraft(new MeterRegistrationDTO());
    setRegIsNew(true);
    setRegErrors({});
  };
  const editRegistration = (row) => {
    setRegDraft(new MeterRegistrationDTO(row));
    setRegIsNew(false);
    setRegErrors({});
  };
  const cancelRegEdit = () => {
    setRegDraft(null);
    setRegIsNew(false);
    setRegErrors({});
  };
  const setRegField = (field, value) =>
    setRegDraft((d) => (d ? new MeterRegistrationDTO({ ...d, [field]: value }) : d));

  const saveRegistration = async () => {
    if (!regDraft) return;
    const { isValid, errors } = validateRegistration(regDraft, registrations);
    setRegErrors(errors);
    if (!isValid) return;
    setSaving(true);
    setError("");
    try {
      const base = {
        MeterSerial: regDraft.meterSerial.trim(),
        CorporateEntityId: regDraft.corporateEntityId,
        CommodityCode: regDraft.commodityCode,
        SiteReference: regDraft.siteReference?.trim() || null,
        SchemeCode: regDraft.schemeCode || null,
        MeterTimeZone: regDraft.meterTimeZone?.trim() || null,
        Description: regDraft.description?.trim() || null,
      };
      if (regIsNew) await addRegistration(base, config);
      else await updateRegistration({ ...base, MeterRegistrationId: regDraft.meterRegistrationId }, config);
      cancelRegEdit();
      await refreshRegistrations();
      setNotice("Meter saved.");
    } catch (err) {
      setError(err?.message || "Could not save the meter.");
    } finally {
      setSaving(false);
    }
  };

  const retireRegistration = async (id) => {
    setError("");
    try {
      await removeRegistration(id, config);
      if (regDraft?.meterRegistrationId === id) cancelRegEdit();
      await refreshRegistrations();
      setNotice("Meter removed.");
    } catch (err) {
      setError(err?.message || "Could not remove the meter.");
    }
  };

  return {
    tab, setTab,
    ref,
    // catalogue
    concepts, loading,
    commodityFilter, setCommodityFilter,
    statusFilter, setStatusFilter,
    search, setSearch,
    refreshConcepts,
    draft, isNew, conceptErrors, saving,
    newConcept, editConcept, cancelEdit, setConceptField, saveConcept, retireConcept,
    idForm, idErrors, setIdField, addIdentifierRow, removeIdentifierRow,
    attrForm, setAttrField, linkAttribute, unlinkAttribute,
    // meters
    registrations, regLoading, refreshRegistrations,
    regDraft, regIsNew, regErrors,
    newRegistration, editRegistration, cancelRegEdit, setRegField, saveRegistration, retireRegistration,
    // page messages
    error, notice,
  };
}
